import {
    useEffect,
    useLayoutEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    Image,
    View,
    Modal
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    RowCard,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    GetCompanyProfilePayloadType
} from "../../../services/restAPI/actions/getCompanyProfile/types";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronRight,
    ChevronLeft,
    X
} from "lucide-react-native";
import {
    GestureHandlerRootView
} from "react-native-gesture-handler";
import {
    ZoomableImage
} from "../../../components";
import {
    RESTService
} from "../../../services/restAPI";

const CompanyDetail = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const route = useRoute<RouteProp<AppStackParamList, "CompanyDetail">>();

    const {
        companyID
    } = route.params;

    const {
        spaces,
        borders,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const inset = useSafeAreaInsets();

    const [data,
        setData] = useState<GetCompanyProfilePayloadType>();
    const [loading,
        setLoading] = useState(true);
    const [isLightboxVisible,
        setIsLightboxVisible] = useState(false);
    const [contentSwitch,
        setContentSwitch] = useState<"general" | "personel" | "contact">("general");

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        paddingTop: inset.top,
                        paddingBottom: spaces.spacingMd,
                        paddingHorizontal: spaces.spacingMd,
                        backgroundColor: colors.content.container.default
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>

            </View>
        });
    }, [navigation]);

    useEffect(() => {
        getCompanyData();
    }, []);

    const getCompanyData = () => {
        RESTService.action("GetCompanyProfile", {
            companyID: companyID
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setData(res.payload);
                    setLoading(false);
                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const renderProfileInfo = () => {
        return <View
            style={[
                stylesheet.profileInfoContainer,
                {
                    paddingTop: spaces.spacingMd,
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <TouchableOpacity
                onPress={() => {
                    setIsLightboxVisible(true);
                }}
            >
                <Image
                    source={{
                        uri: data?.logoURL
                    }}
                    resizeMode="contain"
                    style={[
                        stylesheet.companyLogo,
                        {
                            borderRadius: radiuses.md,
                            backgroundColor: colors.content.container.default,
                            borderWidth: borders.line,
                            borderColor: colors.content.border.subtle,
                            marginRight: spaces.spacingMd
                        }
                    ]}
                />
            </TouchableOpacity>
            <View
                style={stylesheet.profileInfoTextContainer}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                    numberOfLines={2}
                >
                    {data?.title}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    color="low"
                    numberOfLines={2}
                >
                    {data?.address}
                </Text>
            </View>
        </View>;
    };

    const renderContentSwitch = () => {
        return <View
            style={[
                stylesheet.contentSwitchContainer,
                {
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <TouchableOpacity
                style={[
                    stylesheet.contentSwitchContent,
                    {
                        paddingVertical: spaces.spacingMd,
                        paddingHorizontal: spaces.spacingLg,
                        borderRadius: radiuses.actions,
                        backgroundColor: contentSwitch === "general" ? colors.project.secondary : null,
                        marginRight: spaces.spacingMd
                    }
                ]}
                onPress={() => {
                    setContentSwitch("general");
                }}
            >
                <Text
                    variant="titleMediumSize"
                    color={contentSwitch === "general" ? "onPrimary" : "low"}
                >
                    {localize("general")}
                </Text>
            </TouchableOpacity>
            <View
                style={[
                    stylesheet.tabSeparator,
                    {
                        borderRightWidth: borders.line,
                        borderColor: colors.project.brandBorder,
                        marginRight: spaces.spacingMd
                    }
                ]}
            />
            <TouchableOpacity
                style={[
                    stylesheet.contentSwitchContent,
                    {
                        paddingVertical: spaces.spacingMd,
                        paddingHorizontal: spaces.spacingLg,
                        borderRadius: radiuses.actions,
                        backgroundColor: contentSwitch === "personel" ? colors.project.secondary : null,
                        marginRight: spaces.spacingMd
                    }
                ]}
                onPress={() => {
                    setContentSwitch("personel");
                }}
            >
                <Text
                    variant="titleMediumSize"
                    color={contentSwitch === "personel" ? "onPrimary" : "low"}
                >
                    {localize("employees")}
                </Text>
            </TouchableOpacity>
            <View
                style={[
                    stylesheet.tabSeparator,
                    {
                        borderRightWidth: borders.line,
                        borderColor: colors.project.brandBorder,
                        marginRight: spaces.spacingMd
                    }
                ]}
            />
            <TouchableOpacity
                style={[
                    stylesheet.contentSwitchContent,
                    {
                        paddingVertical: spaces.spacingMd,
                        paddingHorizontal: spaces.spacingLg,
                        borderRadius: radiuses.actions,
                        backgroundColor: contentSwitch === "contact" ? colors.project.secondary : null
                    }
                ]}
                onPress={() => {
                    setContentSwitch("contact");
                }}
            >
                <Text
                    variant="titleMediumSize"
                    color={contentSwitch === "contact" ? "onPrimary" : "low"}
                >
                    {localize("contact")}
                </Text>
            </TouchableOpacity>
        </View>;
    };

    const renderAboutCompany = () => {
        if(contentSwitch === "general") {
            return <View
                style={{
                    marginBottom: spaces.spacingLg
                }}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {localize("about")}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    color={data?.aboutUs ? "mid" : "low"}
                >
                    {(data && data.aboutUs && data.aboutUs.length) ? data.aboutUs : localize("notSpecified")}
                </Text>
            </View>;
        } else {
            return null;
        }
    };

    const renderCompanyCampaings = () => {
        if(contentSwitch === "general" && data?.opportunity) {
            return <View
                style={{
                    marginBottom: spaces.spacingXl
                }}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {localize("jobOpportunity")}
                </Text>
                <View
                    style={[
                        stylesheet.campaignImageContainer,
                        {
                            borderRadius: radiuses.form
                        }
                    ]}
                >
                    <Image
                        source={{
                            uri: data.opportunity.coverPhotoURL
                        }}
                        resizeMode="cover"
                        style={[
                            stylesheet.campaignImage,
                            {
                                borderRadius: radiuses.xl
                            }
                        ]}
                    />
                    <View
                        style={[
                            stylesheet.campaignTag,
                            {
                                paddingHorizontal: spaces.spacingMd,
                                paddingVertical: spaces.spacingSm,
                                backgroundColor: colors.content.container.default,
                                borderRadius: radiuses.lg,
                                left: spaces.spacingMd,
                                top: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            color="high"
                            variant="titleMediumSize"
                        >
                            {data?.title}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.campaignBottom,
                            {
                                left: spaces.spacingMd,
                                bottom: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text
                            color="onPrimary"
                            variant="bodyMediumSize"
                        >
                            {data.opportunity.title}
                        </Text>
                        <Text
                            color="onPrimary"
                            variant="bodyMediumSize"
                        >
                            {data.opportunity.description}
                        </Text>
                    </View>
                </View>
            </View>;
        } else {
            return null;
        }
    };

    const renderGeneralInfo = () => {
        if(contentSwitch === "general") {
            let _sectors = data?.sectors?.map(e => e.sectorName).join(", ");
            if (!_sectors || _sectors.trim() === "") {
                _sectors = localize("notSpecified");
            }
            return <View
                style={{
                    marginBottom: spaces.spacingLg,
                    paddingTop: spaces.spacingMd,
                    borderTopWidth: borders.line,
                    borderTopColor: colors.project.brandBorder
                }}
            >
                <View
                    style={[
                        stylesheet.generalInfoContentContainer,
                        {
                            marginBottom: spaces.spacingMd
                        }
                    ]}
                >
                    <View
                        style={stylesheet.generalInfoContent}
                    >
                        <Text
                            variant="bodyLargeSize"
                        >
                            {localize("companyName")}
                        </Text>
                        <Text
                            variant="titleMediumSize"
                            numberOfLines={3}
                        >
                            {data?.title}
                        </Text>
                    </View>

                    <View
                        style={stylesheet.generalInfoContent}
                    >
                        <Text
                            variant="bodyLargeSize"
                        >
                            {localize("sector")}
                        </Text>
                        <Text
                            variant="titleMediumSize"
                            numberOfLines={3}
                        >
                            {_sectors}
                        </Text>
                    </View>
                </View>
                <View
                    style={stylesheet.generalInfoContentContainer}
                >
                    <View
                        style={stylesheet.generalInfoContent}
                    >
                        <Text
                            variant="bodyLargeSize"
                        >
                            {localize("foundationYear")}
                        </Text>
                        <Text
                            variant="titleMediumSize"
                        >
                            {data?.foundationYear ? String(data.foundationYear) : localize("notSpecified")}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.generalInfoContent}
                    >
                        <Text
                            variant="bodyLargeSize"
                        >
                            {localize("export")}
                        </Text>
                        <Text
                            variant="titleMediumSize"
                            numberOfLines={2}
                        >
                            {data?.isCompanyExports ? "Evet" : "Hayır"}
                        </Text>
                    </View>

                </View>
            </View>;
        } else {
            return null;
        }
    };

    const renderPersonels = () => {
        if(contentSwitch === "personel") {
            return <View>
                {data?.people.map(e => {
                    return <RowCard
                        title={e.fullName}
                        subTitle={e.postionDetail.position}
                        rightIcon={() => <ChevronRight/>}
                        titleVariant="titleMediumSize"
                        key={e.userID}
                        onPress={() => {
                            navigation.navigate("UserProfile", {
                                userID: e.userID
                            });
                        }}
                        style={{
                            marginBottom: spaces.spacingMd
                        }}
                        isTransparentBackground={true}
                        icon={() => <View>
                            <Image
                                source={{
                                    uri: e.profilePhotoURL
                                }}
                                resizeMode="cover"
                                style={[
                                    stylesheet.employeePhoto,
                                    {
                                        borderRadius: radiuses.xs
                                    }
                                ]}
                            />
                        </View>}
                    />;
                })}
            </View>;
        } else {
            return null;
        }
    };

    const renderContactInfo = () => {
        if(contentSwitch === "contact") {
            return <View>
                <Text
                    variant="titleLargeSize"
                >
                    {localize("address")}
                </Text>
                <Text>
                    {data?.address}
                </Text>
            </View>;
        } else {
            return null;
        }
    };

    if(loading) {
        return <PageContainer>
            <ActivityIndicator
                size={"large"}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        backgroundColor="default"
        isScrollable={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        {renderProfileInfo()}
        {renderContentSwitch()}
        {renderAboutCompany()}
        {renderCompanyCampaings()}
        {renderGeneralInfo()}
        {renderPersonels()}
        {renderContactInfo()}
        <Modal
            visible={isLightboxVisible}
            transparent={true}
            animationType="fade"
            onRequestClose={() => setIsLightboxVisible(false)}
        >
            <GestureHandlerRootView
                style={stylesheet.flex1}
            >
                <View
                    style={stylesheet.lightboxContainer}
                >
                    <TouchableOpacity
                        style={[
                            stylesheet.lightboxCloseBtn,
                            {
                                top: inset.top + spaces.spacingMd,
                                right: spaces.spacingMd,
                                padding: spaces.spacingSm
                            }
                        ]}
                        onPress={() => setIsLightboxVisible(false)}
                    >
                        <X
                            size={30}
                            color="#FFF"
                        />
                    </TouchableOpacity>
                    {data?.logoURL && (
                        <ZoomableImage
                            uri={data.logoURL}
                        />)}
                </View>
            </GestureHandlerRootView>
        </Modal>
    </PageContainer>;
};
export default CompanyDetail;
